import { tracking } from '@iwoca/frontend-tracking-library';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { STEP_ZERO_STEP_ID } from '@src/containers/Routes/helper/stepActiveIds.helper';
import { useNavigateToStep } from '@src/hooks/useNavigateToStep';
import { useNavigation } from '@src/hooks/useNavigation';
import { AppDispatch, RootState } from '@src/reducers';
import { Step } from '@src/reducers/types/reducers.types';

import { CheckMarkSVG } from './CheckMarkSVG';
import messages from './ProgressSteps.messages';
import styles from './ProgressSteps.module.css';
import { selectStep } from '../../actions';
import {
    getCountryCode,
    hasAutomatedDecision as hasAutomatedDecisionSelector
} from '../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';

const mapStateToProps = (state: RootState) => ({
    hasAutomatedDecision: hasAutomatedDecisionSelector(state),
    countryCode: getCountryCode(state)
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    selectStep: (selectedStep: Step) => dispatch(selectStep(selectedStep))
});

type TProgressSteps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & {
        className?: string;
    };

type TStepMarker = { isComplete: boolean; isCurrentStep?: boolean };

const ProgressSteps = ({
    hasAutomatedDecision,
    className,
    countryCode,
    selectStep
}: TProgressSteps) => {
    const { navigateToStep, currentStepId } = useNavigateToStep();
    const { progressIndicatorSteps } = useNavigation();

    const currentStepIndex = progressIndicatorSteps.indexOf(currentStepId);

    const onSelect = (selectedStepId: string) => {
        const selectedStepIndex =
            progressIndicatorSteps.indexOf(selectedStepId);

        if (selectedStepIndex > currentStepIndex) return;

        selectStep({ id: selectedStepId });
        navigateToStep(selectedStepId);

        tracking.ampli.progressBarClicked({
            step_to: selectedStepId,
            ...getAmplitudeDefaultEventProperties()
        });
    };

    const renderStep = (stepId: string, index: number) => {
        const isInstantDecision =
            hasAutomatedDecision &&
            countryCode === 'de' &&
            stepId === 'businessAccounts';

        const stepContent = isInstantDecision ? (
            <FormattedMessage {...messages.instantDecision} />
        ) : (
            <FormattedMessage {...messages[stepId as keyof typeof messages]} />
        );

        return (
            <button
                key={index}
                data-testid={
                    isInstantDecision
                        ? 'instantDecision-step'
                        : `${stepId}-step`
                }
                className={classnames(styles.step, {
                    [styles.firstStep]: stepId === STEP_ZERO_STEP_ID
                })}
                onClick={() => onSelect(stepId)}
                disabled={stepId === STEP_ZERO_STEP_ID && countryCode === 'de'}>
                <StepMarker
                    isCurrentStep={currentStepIndex === index}
                    isComplete={currentStepIndex > index}
                />
                {stepContent}
            </button>
        );
    };

    return (
        <div
            className={classnames(styles.ProgressSteps, className)}
            data-testid="progress-steps">
            {progressIndicatorSteps.map((stepId: string, index: number) =>
                renderStep(stepId, index)
            )}
        </div>
    );
};

const StepMarker = ({ isComplete, isCurrentStep }: TStepMarker) => {
    const stepMarkerStyles = classnames(
        styles.StepMarker,
        isComplete && styles.complete,
        isCurrentStep && styles.currentStep
    );
    return (
        <div className={stepMarkerStyles}>
            {isComplete && <CheckMarkSVG data-testid="completed-step-icon" />}
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProgressSteps);
